import React, { useState, useEffect, useMemo, useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import { expandTableRange, MAX_LENGTH, MAX_TABLES } from './tableRange';

/**
 * "Add a range of tables" — T01 to T35 in one go instead of one at a time.
 *
 * The range is expanded here and the final numbers are sent; the preview shows them
 * before saving. Numbers the outlet already has are greyed out and skipped (the API
 * skips them too), so the same range can be run again after adding a few by hand.
 * No seats are asked for: covers are never checked against them, and tables are
 * joined at service time.
 */
interface Props {
  isOpen: boolean;
  /** Numbers the outlet already has, any case. */
  existingNumbers: string[];
  sectionOptions: { value: string; label: string }[];
  onSaved: () => void;
  onClose: () => void;
}

const DineOpsTableRangeModal: React.FC<Props> = ({ isOpen, existingNumbers, sectionOptions, onSaved, onClose }) => {
  const [prefix, setPrefix] = useState('T');
  const [from, setFrom] = useState('1');
  const [to, setTo] = useState('');
  const [pad, setPad] = useState(true);
  const [sectionId, setSectionId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setPrefix('T'); setFrom('1'); setTo(''); setPad(true); setSectionId(''); setError(null);
  }, [isOpen]);

  const existing = useMemo(() => new Set(existingNumbers.map((n) => n.toLowerCase())), [existingNumbers]);

  const numbers = useMemo(
    () => expandTableRange(prefix.trim(), Number(from), Number(to), pad),
    [prefix, from, to, pad],
  );
  const fresh = useMemo(() => numbers.filter((n) => !existing.has(n.toLowerCase())), [numbers, existing]);

  const problem = useMemo((): string | null => {
    if (from === '' || to === '') return null;
    if (!Number.isInteger(Number(from)) || !Number.isInteger(Number(to)) || Number(from) < 0) return 'From and To are whole numbers.';
    if (Number(to) < Number(from)) return 'To cannot be smaller than From.';
    if (numbers.length > MAX_TABLES) return `Add at most ${MAX_TABLES} tables at a time.`;
    if (numbers.some((n) => n.length > MAX_LENGTH)) return `A table number is at most ${MAX_LENGTH} characters. Shorten the prefix.`;
    return null;
  }, [from, to, numbers]);

  const save = useCallback(async () => {
    if (problem || fresh.length === 0) return;
    setSaving(true); setError(null);
    try {
      const res = await dineOpsService.bulkCreateTables({ tableNumbers: fresh, sectionId: sectionId || undefined });
      notificationService.success(
        'Tables added',
        `${res.created.length} table(s) added${res.skipped.length ? `, ${res.skipped.length} already existed` : ''}.`,
      );
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'The tables could not be added.');
    } finally {
      setSaving(false);
    }
  }, [problem, fresh, sectionId, onSaved]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container" style={{ minHeight: 'auto', maxWidth: 560 }}>
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Add a range of tables</h2>
          <button className="modal-close" onClick={onClose} disabled={saving}><IconSystem.x size={18} /></button>
        </div>

        <div className="modal-content">
          {error && <div className="form-error-message" style={{ marginBottom: 12 }}>{error}</div>}

          <div className="form-grid-3">
            <div className="form-group">
              <label className="form-label">Prefix</label>
              <input className="form-input" value={prefix} maxLength={10} placeholder="none"
                onChange={(e) => setPrefix(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">From <span className="text-danger">*</span></label>
              <input className="form-input" type="number" min={0} value={from}
                onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">To <span className="text-danger">*</span></label>
              <input className="form-input" type="number" min={0} value={to} autoFocus placeholder="e.g., 35"
                onChange={(e) => setTo(e.target.value)} />
            </div>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: 13 }}>
            <input type="checkbox" checked={pad} onChange={(e) => setPad(e.target.checked)} />
            Pad with zeros ({prefix.trim()}01 rather than {prefix.trim()}1)
          </label>

          <div className="form-group">
            <label className="form-label">Section</label>
            <SyutSelect options={sectionOptions} value={sectionId} size="sm" menuPortalTarget={document.body}
              onChange={(v) => setSectionId((v as string) || '')} placeholder="No section" />
          </div>

          {problem && <div className="form-error-message" style={{ marginBottom: 8 }}>{problem}</div>}

          {!problem && numbers.length > 0 && (
            <>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
                {fresh.length} new{numbers.length > fresh.length ? ` · ${numbers.length - fresh.length} already exist and will be skipped` : ''}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxHeight: 160, overflowY: 'auto' }}>
                {numbers.map((n) => {
                  const taken = existing.has(n.toLowerCase());
                  return (
                    <span key={n} className="badge" title={taken ? 'Already exists — skipped' : undefined}
                      style={{ opacity: taken ? 0.4 : 1, textDecoration: taken ? 'line-through' : undefined }}>
                      {n}
                    </span>
                  );
                })}
              </div>
            </>
          )}

          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>
            Seats are not asked for: the party size is never checked against them, and tables can be joined during service.
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={() => void save()} disabled={saving || !!problem || fresh.length === 0}>
            {saving ? 'Adding…' : fresh.length ? `Add ${fresh.length} table${fresh.length === 1 ? '' : 's'}` : 'Add tables'}
          </button>
        </div>
      </div>
    </>
  );
};

export default DineOpsTableRangeModal;
